import { PageHeader } from '@/design-system/page-header'
import { Card, CardContent, CardHeader, CardTitle } from '@/design-system/card'
import { Badge } from '@/design-system/badge'
import { segments } from '@/consultant-study/segments'
import { microprofiles } from '@/consultant-study/microprofiles'
import {
  ArrowRight, Target, XCircle, Shuffle, UserCheck,
  FileBarChart, Brain, AlertTriangle, TrendingUp, Activity,
} from 'lucide-react'

const matchingSteps = [
  { title: 'Survey Profile', desc: '14-factor behavioral fingerprint for each consultant' },
  { title: 'Factor Matching', desc: 'Algorithm evaluates factor scores against lead requirements' },
  { title: 'Lead Type Alignment', desc: 'Maps each consultant to best/avoid lead categories' },
  { title: 'Assignment', desc: 'Consultant receives behaviorally matched leads' },
]

const matchingExamples = [
  {
    profile: 'The Trust Architect (S1-A)',
    leads: 'Families & first-time buyers',
    reason: 'Highest client trust (F4=0.66), patient closing style builds confidence with anxious first-time buyers.',
  },
  {
    profile: 'The Closing Machine (S1-B)',
    leads: 'Time-sensitive campaign leads',
    reason: 'Strongest closer (F11=0.98), reads buying signals instantly. Thrives under pressure.',
  },
  {
    profile: 'The Health Domain Specialist (S1-C)',
    leads: 'Health insurance inquiries',
    reason: 'Exceptional health expertise (F9=1.75). Deep domain authority converts health-conscious clients.',
  },
  {
    profile: 'The Balanced Value Seller (S2-A)',
    leads: 'Affluent benefit-conscious clients',
    reason: 'Sells through value, not pressure. Low price worry (F2=-0.85) means never discounts.',
  },
  {
    profile: 'The Warm Drifter (S4-B)',
    leads: 'Warm referrals & community leads',
    reason: 'Genuine personal warmth (F6=0.39). Converts at 14% when the lead comes warm.',
  },
  {
    profile: 'The Dormant Mobilizer (S5-A)',
    leads: 'Structured, time-bound lead batches',
    reason: 'Strongest mobilization reflex (F7=0.96). Zero stress. Needs activation signal, not capacity.',
  },
]

const impactCards = [
  {
    label: 'Baseline',
    value: '11.8%',
    description: 'Current average conversion with random lead assignment',
    color: '#6B7280',
    bg: 'bg-gray-50',
    projected: false,
  },
  {
    label: '+5-8% with Survey Matching',
    value: '~16-20%',
    description: 'Behavioral profiling + rule-based lead matching using existing survey data',
    color: '#F17922',
    bg: 'bg-[#F17922]/5',
    projected: true,
  },
  {
    label: '+12-18% with CRM + ML',
    value: '~24-30%',
    description: 'CRM-verified profiles + ML-optimized matching with continuous learning',
    color: '#2563EB',
    bg: 'bg-blue-50',
    projected: true,
  },
]

const roadmapPhases = [
  {
    phase: 1,
    title: 'Rule-Based Matching',
    when: 'Now',
    color: '#F17922',
    items: [
      'Use existing survey data and microprofile assignments',
      'Map each consultant to their best/avoid lead categories',
      'Build simple routing rules in lead management system',
      'Measure conversion lift vs. random assignment baseline',
    ],
  },
  {
    phase: 2,
    title: 'CRM-Verified Matching',
    when: 'After CRM integration',
    color: '#2563EB',
    items: [
      'Replace self-reported metrics with verified CRM data',
      'Refine segment boundaries using actual conversion rates',
      'Add behavioral CRM signals (response time, follow-up patterns)',
      'A/B test matched vs. random assignment in controlled rollout',
    ],
  },
  {
    phase: 3,
    title: 'ML-Optimized Matching',
    when: 'Continuous improvement',
    color: '#8B5CF6',
    items: [
      'Train ML models on matched outcomes to optimize assignments',
      'Predict conversion probability for every consultant-lead pair',
      'Incorporate temporal patterns (time of day, season, campaign)',
      'Self-improving system that learns from every interaction',
    ],
  },
]

const predictions = [
  {
    title: 'Churn Risk',
    description: 'Predict which consultants will leave in 6 months using engagement decay patterns and satisfaction trends.',
    icon: AlertTriangle,
    color: 'text-red-500',
    bg: 'bg-red-50',
  },
  {
    title: 'Burnout Detection',
    description: 'Flag high-performing consultants at risk of collapse. References S3-A microprofile patterns as early warning signals.',
    icon: Activity,
    color: 'text-amber-500',
    bg: 'bg-amber-50',
  },
  {
    title: 'Upsell Potential',
    description: 'Identify which consultants can expand client portfolios based on product knowledge and client retention data.',
    icon: TrendingUp,
    color: 'text-emerald-500',
    bg: 'bg-emerald-50',
  },
  {
    title: 'Revenue Optimization',
    description: 'Optimal lead-consultant matching for maximum conversion through behavioral and contextual alignment.',
    icon: Target,
    color: 'text-blue-500',
    bg: 'bg-blue-50',
  },
]

export default function MatchingPage() {
  return (
    <div className="max-w-7xl mx-auto px-6 py-8 space-y-12">
      <PageHeader
        title="Matching Vision"
        subtitle="From random lead assignment to behavioral matching"
      />

      {/* Hero intro */}
      <section>
        <Card className="border-l-4 border-l-[#F17922] bg-[#F17922]/[0.02]">
          <CardContent className="py-6">
            <p className="text-sm leading-relaxed text-gray-700">
              The most immediate value from this study is smarter lead allocation. Today, leads are
              assigned by geography or availability. With behavioral profiling, we can match leads to
              the consultant most likely to convert them. This section explains how the matching works,
              shows example assignments, and projects the expected impact. This can start now.
            </p>
          </CardContent>
        </Card>
      </section>

      {/* The Problem: Before / After */}
      <section>
        <h2 className="text-lg font-semibold mb-4">The Problem</h2>
        <div className="grid gap-6 md:grid-cols-2">
          <Card className="border-dashed">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center bg-gray-100">
                  <Shuffle className="h-5 w-5 text-gray-500" />
                </div>
                <div>
                  <Badge variant="secondary" className="mb-1">Current State</Badge>
                  <CardTitle className="text-base">Random Assignment</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-gray-400">--</span>
                  Leads assigned by geography or next-available
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-gray-400">--</span>
                  No consideration of consultant behavioral strengths
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-gray-400">--</span>
                  Anxious first-buyers may get an aggressive closer
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-gray-400">--</span>
                  Average conversion: 11.8%
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-[#F17922]/30 bg-[#F17922]/[0.02]">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center bg-[#F17922]/10">
                  <UserCheck className="h-5 w-5 text-[#F17922]" />
                </div>
                <div>
                  <Badge className="mb-1 border-[#F17922]/20 bg-[#F17922]/10 text-[#F17922]">
                    Proposed
                  </Badge>
                  <CardTitle className="text-base">Behavioral Matching</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-[#F17922]">+</span>
                  Leads matched to consultant behavioral profile
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-[#F17922]">+</span>
                  Strengths aligned to lead requirements
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-[#F17922]">+</span>
                  Anxious first-buyers get the Trust Architect
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-[#F17922]">+</span>
                  Projected conversion: 16-20%+
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* The Matching Algorithm */}
      <section>
        <h2 className="text-lg font-semibold mb-4">The Matching Algorithm</h2>
        <Card>
          <CardContent className="py-6 space-y-8">
            {/* Flow diagram */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              {matchingSteps.map((step, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="border-2 border-[#F17922] p-4 w-52 text-center">
                    <div className="font-semibold text-gray-900 mb-1">{step.title}</div>
                    <div className="text-xs text-gray-500">{step.desc}</div>
                  </div>
                  {i < matchingSteps.length - 1 && (
                    <ArrowRight className="size-5 shrink-0 text-[#F17922]" />
                  )}
                </div>
              ))}
            </div>

            {/* Text explanation */}
            <div className="space-y-3 text-sm text-gray-700 max-w-3xl">
              <p>
                <span className="font-semibold">Step 1:</span> Every consultant has a 14-factor
                behavioral fingerprint derived from the survey. This is their immutable profile.
              </p>
              <p>
                <span className="font-semibold">Step 2:</span> Each lead type has behavioral
                requirements — anxious first-buyers need high client trust (F4), time-sensitive
                campaigns need assertive closing (F11).
              </p>
              <p>
                <span className="font-semibold">Step 3:</span> The algorithm scores every
                consultant-lead combination, producing a compatibility ranking.
              </p>
              <p>
                <span className="font-semibold">Step 4:</span> Leads are routed to the highest-ranked
                available consultant, with avoidance rules preventing known mismatches.
              </p>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Example Matches */}
      <section>
        <h2 className="text-lg font-semibold mb-4">Example Matches</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {matchingExamples.map((ex, i) => (
            <Card key={i}>
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <span className="mt-1 w-2 h-2 bg-[#F17922] shrink-0" />
                  <div>
                    <p className="text-sm">
                      <span className="font-semibold text-gray-900">{ex.profile}</span>
                      <span className="text-[#F17922] font-bold mx-2">&rarr;</span>
                      <span className="font-semibold text-gray-900">{ex.leads}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{ex.reason}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Expected Impact */}
      <section>
        <h2 className="text-lg font-semibold mb-4">Expected Impact</h2>
        <div className="grid gap-6 md:grid-cols-3">
          {impactCards.map((card) => (
            <Card key={card.label} className={card.bg}>
              <CardContent className="py-6 text-center">
                {card.projected && (
                  <Badge variant="outline" className="mb-2 text-[10px] text-amber-600 border-amber-300">Projected</Badge>
                )}
                <div className="text-3xl font-bold" style={{ color: card.color }}>
                  {card.value}
                </div>
                <div className="text-sm font-semibold text-gray-900 mt-2">{card.label}</div>
                <p className="text-xs text-gray-500 mt-2">{card.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Implementation Roadmap */}
      <section>
        <h2 className="text-lg font-semibold mb-4">Implementation Roadmap</h2>
        <div className="grid gap-6 md:grid-cols-3">
          {roadmapPhases.map((phase) => (
            <Card
              key={phase.phase}
              className="relative overflow-hidden"
              style={{ borderTopWidth: 4, borderTopColor: phase.color }}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className="text-xs"
                    style={{ borderColor: phase.color, color: phase.color }}
                  >
                    Phase {phase.phase}
                  </Badge>
                  <span className="text-xs text-gray-400">{phase.when}</span>
                </div>
                <CardTitle className="text-base mt-1">{phase.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {phase.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="mt-1.5 w-1.5 h-1.5 shrink-0" style={{ backgroundColor: phase.color }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Full Matching Matrix */}
      <section>
        <h2 className="text-lg font-semibold mb-4">Full Matching Matrix</h2>
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left bg-gray-50">
                    <th className="px-4 py-3 font-medium">Profile</th>
                    <th className="px-4 py-3 font-medium">Nickname</th>
                    <th className="px-4 py-3 font-medium text-right">Conv%</th>
                    <th className="px-4 py-3 font-medium">
                      <span className="flex items-center gap-1">
                        <Target className="h-3 w-3 text-green-600" /> Best Leads
                      </span>
                    </th>
                    <th className="px-4 py-3 font-medium">
                      <span className="flex items-center gap-1">
                        <XCircle className="h-3 w-3 text-red-600" /> Avoid
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {microprofiles.map((mp) => {
                    const seg = segments.find((s) => s.id === mp.segmentId)
                    return (
                      <tr key={mp.id} className="border-b border-border/50 last:border-0">
                        <td className="px-4 py-3">
                          <span
                            className="px-1.5 py-0.5 text-xs font-bold text-white"
                            style={{ backgroundColor: seg?.color }}
                          >
                            {mp.id}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium">{mp.nickname}</td>
                        <td className="px-4 py-3 text-right font-mono">{mp.conversionRate}%</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {mp.bestLeads.slice(0, 3).map((lead) => (
                              <span
                                key={lead}
                                className="bg-green-50 px-1.5 py-0.5 text-[10px] font-medium text-green-700 border border-green-200"
                              >
                                {lead}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {mp.avoidLeads.slice(0, 2).map((lead) => (
                              <span
                                key={lead}
                                className="bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700 border border-red-200"
                              >
                                {lead}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* ============================================================ */}
      {/*  What Comes Next (merged from Future Roadmap)                */}
      {/* ============================================================ */}
      <section>
        <h2 className="text-xl font-bold mb-6">What Comes Next</h2>

        {/* Enrichment Leap: Before / After */}
        <h3 className="text-base font-semibold mb-4">The Enrichment Leap</h3>
        <div className="grid gap-6 md:grid-cols-2 mb-8">
          <Card className="border-dashed">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center bg-gray-100">
                  <FileBarChart className="h-5 w-5 text-gray-500" />
                </div>
                <div>
                  <Badge variant="secondary" className="mb-1">Current</Badge>
                  <CardTitle className="text-base">Survey-Only</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-gray-400">--</span>
                  Self-reported performance data
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-gray-400">--</span>
                  5 segments, 12 microprofiles
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-gray-400">--</span>
                  Factor analysis on attitudes
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-gray-400">--</span>
                  Snapshot in time, no trends
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="border-[#F17922]/30 bg-[#F17922]/[0.02]">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center bg-[#F17922]/10">
                  <Brain className="h-5 w-5 text-[#F17922]" />
                </div>
                <div>
                  <Badge className="mb-1 border-[#F17922]/20 bg-[#F17922]/10 text-[#F17922]">
                    With CRM
                  </Badge>
                  <CardTitle className="text-base">Surveys + CRM for 1,000+ Consultants</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-[#F17922]">+</span>
                  Verified revenue, retention, conversion metrics
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-[#F17922]">+</span>
                  Behavioral patterns from real interactions
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-[#F17922]">+</span>
                  Predictive models: churn, burnout, upsell
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 text-[#F17922]">+</span>
                  Longitudinal trends and early warning signals
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Prediction Vision Cards */}
        <h3 className="text-base font-semibold mb-4">Prediction Capabilities</h3>
        <div className="grid gap-6 md:grid-cols-2 mb-8">
          {predictions.map((pred) => {
            const Icon = pred.icon
            return (
              <Card key={pred.title}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className={`flex h-10 w-10 items-center justify-center ${pred.bg}`}>
                      <Icon className={`h-5 w-5 ${pred.color}`} />
                    </div>
                    <CardTitle className="text-base">{pred.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{pred.description}</p>
                </CardContent>
              </Card>
            )
          })}
        </div>

        {/* Research Iterations */}
        <Card className="border-l-4 border-l-amber-500 bg-amber-50/50">
          <CardHeader>
            <CardTitle className="text-base">Research Iterations</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground leading-relaxed">
              This is not a one-time study. The research design supports annual re-survey to track
              behavioral drift, segment migration, and coaching effectiveness. Each iteration refines
              the factor model, validates segment stability, and measures whether interventions
              (mentorship, lead matching, digital training) are moving consultants between segments.
              Over time, the system becomes self-correcting: survey findings inform interventions,
              CRM data measures outcomes, and ML models optimize continuously.
            </p>
          </CardContent>
        </Card>
      </section>
    </div>
  )
}
